import { useEffect, useMemo, useRef, useState } from "react";
import { ExtendedMessageDto } from "../../data/use-messages-data";

interface MessageSet {
  userMessage?: ExtendedMessageDto;
  responseMessages: ExtendedMessageDto[];
}

export function useMessageSets(
  messages: ExtendedMessageDto[],
  options?: { followLatest?: boolean },
) {
  const messageSets = useMemo(() => {
    const sets: MessageSet[] = [];
    let i = 0;

    while (i < messages.length) {
      // Look for a human message to start a new set
      if (i === 0 || messages[i].type === "user") {
        const userMessage =
          messages[i].type === "user" ? messages[i] : undefined;
        const responseMessages: ExtendedMessageDto[] = [];

        // Collect all subsequent non-human messages (ai, tool, system, etc.)
        let j = i + (messages[i].type === "user" ? 1 : 0);
        while (j < messages.length && messages[j].type !== "user") {
          responseMessages.push(messages[j]);
          j++;
        }

        sets.push({
          userMessage,
          responseMessages,
        });

        // Move to the next potential human message
        i = j;
      } else {
        // Skip non-human messages that don't have a preceding human message
        i++;
      }
    }

    return sets;
  }, [messages]);

  const [index, setIndex] = useState(0);
  const previousSets = useRef(messageSets.length);
  const isFirstRender = useRef(true);

  // Set index to latest set when messages.length increases
  useEffect(() => {
    if (!options?.followLatest) return;
    if (messageSets.length === 1) {
      setIndex(0);
      return;
    }
    if (isFirstRender.current && messageSets.length > 1) {
      setIndex(messageSets.length - 1);
    }
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else if (messageSets.length > previousSets.current) {
      setIndex(messageSets.length - 1);
    }
    previousSets.current = messageSets.length;
  }, [messages.length, messageSets.length, options?.followLatest]);

  useEffect(() => {
    if (index >= messageSets.length && messageSets.length > 0) {
      setIndex(messageSets.length - 1);
    }
  }, [index, messageSets.length]);

  const next = () => setIndex((i) => Math.min(i + 1, messageSets.length - 1));
  const previous = () => setIndex((i) => Math.max(i - 1, 0));
  const reset = () => setIndex(0);

  // Index updates in an effect, so a shrinking list renders once with an
  // index past the end. Clamp here so that render still has a set.
  const boundedIndex =
    messageSets.length === 0
      ? 0
      : Math.min(Math.max(index, 0), messageSets.length - 1);

  return {
    index: boundedIndex,
    set: messageSets[boundedIndex],
    next,
    previous,
    reset,
    count: messageSets.length,
  };
}
